import { useDismiss } from "@/common/hooks/useDismiss";
import type { AccountRole } from "@/features/accounts/api/accountsApi";
import { useInvitationsList } from "@/features/accounts/api/invitationsApi";
import type { InvitationStatus } from "@/features/accounts/api/invitationsApi";
import { useMembersList } from "@/features/accounts/api/membersApi";
import { ChangeRoleDialog } from "@/features/accounts/components/ChangeRoleDialog";
import { InviteMemberDialog } from "@/features/accounts/components/InviteMemberDialog";
import { RemoveMemberDialog } from "@/features/accounts/components/RemoveMemberDialog";
import { RoleBadge } from "@/features/accounts/components/RoleBadge";
import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { MoreHorizontal, Plus } from "lucide-react";
import { useRef, useState } from "react";

type ActiveMenu =
  | null
  | { kind: "invite" }
  | { kind: "change-role"; membershipId: string; memberEmail: string; currentRole: AccountRole }
  | { kind: "remove"; membershipId: string; memberEmail: string };

const INVITE_STATUS_LABEL: Record<InvitationStatus, string> = {
  pending: "Pendiente",
  accepted: "Aceptada",
  rejected: "Rechazada",
  cancelled: "Cancelada",
  auto_cancelled: "Auto-cancelada",
  expired: "Expirada",
};

const INVITE_STATUS_CLASS: Record<InvitationStatus, string> = {
  pending: "bg-amber-100 text-amber-800",
  accepted: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-700",
  cancelled: "bg-neutral-100 text-neutral-500",
  auto_cancelled: "bg-neutral-200 text-neutral-500",
  expired: "bg-neutral-200 text-neutral-600",
};

function daysUntil(dateStr: string): string {
  const diff = Math.ceil((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return "expirada";
  if (diff === 1) return "1 día";
  return `${diff} días`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("es-CR", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function MembersPage() {
  const { currentAccount, currentMembership } = useCurrentAccount();
  const [activeMenu, setActiveMenu] = useState<ActiveMenu>(null);
  const [openRowMenu, setOpenRowMenu] = useState<string | null>(null);

  const accountId = currentAccount?.id ?? "";
  const membersQuery = useMembersList(accountId);
  const invitationsQuery = useInvitationsList(accountId);

  const currentUserRole = currentMembership?.role ?? "viewer";
  const canManage = currentUserRole === "owner" || currentUserRole === "admin";

  const visibleInvitations = (invitationsQuery.data ?? []).filter(
    (inv) => inv.status === "pending" || inv.status === "auto_cancelled",
  );

  return (
    <>
      <title>Miembros · HATO</title>

      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <h1 className="font-semibold text-2xl text-app-ink-1 tracking-tight">Miembros</h1>
          {canManage && (
            <button
              type="button"
              onClick={() => setActiveMenu({ kind: "invite" })}
              className="inline-flex items-center gap-1.5 rounded-md bg-app-ink-1 px-3.5 py-2 font-medium text-[13px] text-white hover:bg-[#1e211d]"
            >
              <Plus className="h-3.5 w-3.5" />
              Invitar miembro
            </button>
          )}
        </div>

        {/* Members table */}
        {membersQuery.isPending && <Spinner />}
        {membersQuery.isError && <ErrorMsg text="Error al cargar miembros. Recargá la página." />}

        {membersQuery.data && (
          <section>
            <p className="mb-2 font-medium text-app-ink-3 text-xs uppercase tracking-wider">
              Miembros ({membersQuery.data.length})
            </p>
            <div className="overflow-hidden rounded-2xl border border-app-border bg-app-surface">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-app-border border-b bg-app-surface-2">
                    <Th>Email</Th>
                    <Th>Rol</Th>
                    <Th>Estado</Th>
                    <Th className="hidden sm:table-cell">Miembro desde</Th>
                    {canManage && <Th />}
                  </tr>
                </thead>
                <tbody>
                  {membersQuery.data.map((member) => {
                    const isSelf = member.id === currentMembership?.id;
                    const canEditMember =
                      canManage &&
                      !isSelf &&
                      !(currentUserRole === "admin" && member.role === "owner");

                    return (
                      <tr key={member.id} className="border-app-border border-b last:border-0">
                        <td className="px-4 py-3 text-app-ink-1">{member.user.email}</td>
                        <td className="px-4 py-3">
                          <RoleBadge role={member.role} />
                        </td>
                        <td className="px-4 py-3 text-app-ink-2 text-xs capitalize">
                          {member.status}
                        </td>
                        <td className="hidden px-4 py-3 text-app-ink-3 text-xs sm:table-cell">
                          {formatDate(member.createdAt)}
                        </td>
                        {canManage && (
                          <td className="px-4 py-3">
                            {canEditMember && (
                              <MemberRowMenu
                                isOpen={openRowMenu === member.id}
                                onToggle={() =>
                                  setOpenRowMenu((prev) => (prev === member.id ? null : member.id))
                                }
                                onClose={() => setOpenRowMenu(null)}
                                onChangeRole={() => {
                                  setOpenRowMenu(null);
                                  setActiveMenu({
                                    kind: "change-role",
                                    membershipId: member.id,
                                    memberEmail: member.user.email,
                                    currentRole: member.role,
                                  });
                                }}
                                onRemove={() => {
                                  setOpenRowMenu(null);
                                  setActiveMenu({
                                    kind: "remove",
                                    membershipId: member.id,
                                    memberEmail: member.user.email,
                                  });
                                }}
                              />
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Pending invitations */}
        {invitationsQuery.data && visibleInvitations.length > 0 && (
          <section>
            <p className="mb-2 font-medium text-app-ink-3 text-xs uppercase tracking-wider">
              Invitaciones pendientes ({visibleInvitations.length})
            </p>
            <div className="overflow-hidden rounded-2xl border border-app-border bg-app-surface">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-app-border border-b bg-app-surface-2">
                    <Th>Email</Th>
                    <Th>Rol</Th>
                    <Th>Estado</Th>
                    <Th className="hidden sm:table-cell">Expira</Th>
                  </tr>
                </thead>
                <tbody>
                  {visibleInvitations.map((inv) => (
                    <tr key={inv.id} className="border-app-border border-b last:border-0">
                      <td className="px-4 py-3 text-app-ink-1">{inv.email}</td>
                      <td className="px-4 py-3">
                        <RoleBadge role={inv.role} />
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-medium text-xs ${INVITE_STATUS_CLASS[inv.status]}`}
                          title={
                            inv.status === "auto_cancelled"
                              ? "Cancelada por el sistema (el email ya es miembro o duplicate)"
                              : undefined
                          }
                        >
                          {INVITE_STATUS_LABEL[inv.status]}
                        </span>
                      </td>
                      <td className="hidden px-4 py-3 text-app-ink-3 text-xs sm:table-cell">
                        {inv.status === "pending" ? daysUntil(inv.expiresAt) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      {/* Dialogs */}
      <InviteMemberDialog
        accountId={accountId}
        open={activeMenu?.kind === "invite"}
        onClose={() => setActiveMenu(null)}
      />

      {activeMenu?.kind === "change-role" && (
        <ChangeRoleDialog
          accountId={accountId}
          membershipId={activeMenu.membershipId}
          memberEmail={activeMenu.memberEmail}
          currentRole={activeMenu.currentRole}
          currentUserRole={currentUserRole}
          open
          onClose={() => setActiveMenu(null)}
        />
      )}

      {activeMenu?.kind === "remove" && (
        <RemoveMemberDialog
          accountId={accountId}
          accountName={currentAccount?.name ?? ""}
          membershipId={activeMenu.membershipId}
          memberEmail={activeMenu.memberEmail}
          open
          onClose={() => setActiveMenu(null)}
        />
      )}
    </>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={`px-4 py-3 text-left font-medium text-[11px] text-app-ink-3 uppercase tracking-wider ${className ?? ""}`}
    >
      {children}
    </th>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-app-border border-t-app-emerald" />
    </div>
  );
}

function ErrorMsg({ text }: { text: string }) {
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm">
      {text}
    </div>
  );
}

interface MemberRowMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onChangeRole: () => void;
  onRemove: () => void;
}

function MemberRowMenu({ isOpen, onToggle, onClose, onChangeRole, onRemove }: MemberRowMenuProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  useDismiss({ enabled: isOpen, containerRef, onDismiss: onClose });

  return (
    <div ref={containerRef} className="relative flex justify-end">
      <button
        type="button"
        aria-label="Opciones"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={onToggle}
        className="rounded-md p-1 text-app-ink-3 hover:bg-app-surface-2 hover:text-app-ink-1"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {isOpen && (
        <div className="absolute top-full right-0 z-20 mt-1 w-44 overflow-hidden rounded-xl border border-app-border bg-app-surface shadow">
          <button
            type="button"
            className="w-full px-4 py-2.5 text-left text-app-ink-1 text-sm transition-colors hover:bg-app-surface-2"
            onClick={onChangeRole}
          >
            Cambiar rol
          </button>
          <button
            type="button"
            className="w-full px-4 py-2.5 text-left text-rose-600 text-sm transition-colors hover:bg-rose-50"
            onClick={onRemove}
          >
            Remover
          </button>
        </div>
      )}
    </div>
  );
}
